import { useMemo } from 'react';
import { useApi } from '@shopify/ui-extensions-react/customer-account';
import { useOrderDetails } from './useOrderDetails';
import { getOrderIdFromUrl } from '../utils/getOrderIdFromUrl';
import { calculateSeatUploadRequirements } from '../utils/seatRequirements';
import {
  UseOrderStateProps,
  UseOrderStateReturn,
} from '../types';

/**
 * Custom hook for managing order state and seat requirements
 * @param props - Configuration object with optional orderId
 * @returns Order state and seat requirements data
 */
export const useOrderState = ({ orderId }: UseOrderStateProps): UseOrderStateReturn => {
  const api = useApi<'customer-account.order-status.block.render'>();

  const apiOrderId = api.order?.current?.id;
  const urlOrderId = getOrderIdFromUrl();
  const finalOrderId = orderId || apiOrderId || urlOrderId;

  const {
    orderDetails,
    loading: orderLoading,
    error: orderError,
  } = useOrderDetails(finalOrderId);

  const seatRequirements = useMemo(() => {
    if (!orderDetails) return null;
    return calculateSeatUploadRequirements(orderDetails);
  }, [orderDetails]);

  const hasSeatProducts = useMemo(() => {
    return seatRequirements !== null;
  }, [seatRequirements]);

  return {
    finalOrderId,
    orderDetails,
    orderLoading,
    orderError,
    seatRequirements,
    hasSeatProducts,
  };
};